import { MESSAGE_TYPE } from 'shared/constants/messages';
import { applyPageFilters, applyRoleFilter, sortComparator } from './helpers';
import filterQueryGenerator from 'dashboard/helper/filterQueryGenerator';
import { matchesFilters } from './helpers/filterHelpers';
import {
  getUserPermissions,
  getUserRole,
} from '../../../helper/permissionsHelper';
import camelcaseKeys from 'camelcase-keys';

export const getSelectedChatConversation = ({
  allConversations,
  selectedChatId,
}) =>
  allConversations.filter(conversation => conversation.id === selectedChatId);

// Eltafouk: mirror of the backend ConversationFinder#set_inboxes guard.
// Aggregate views (Mine / Unassigned / All without an inboxId) must
// never surface comment-inbox conversations — those live in their own
// sidebar section ("التعليقات") and are reached by explicitly picking
// a comment inbox.
//
// IMPORTANT: when the user IS explicitly viewing a comment inbox
// (activeFilters.inboxId points to that inbox), the filter must NOT
// fire — otherwise every conversation in that inbox is dropped and the
// list stays empty / stuck on the loading spinner forever. Mirrors the
// backend's `if params[:inbox_id]` short-circuit in ConversationFinder.
const isCommentInboxConversation = (
  rootGetters,
  conversation,
  viewingInboxId
) => {
  const inboxId = conversation?.inbox_id;
  if (!inboxId) return false;
  if (viewingInboxId && Number(viewingInboxId) === Number(inboxId))
    return false;
  const inbox = rootGetters['inboxes/getInbox'](inboxId);
  return Boolean(inbox && inbox.is_comment_inbox);
};

const getters = {
  getAllConversations: ({ allConversations, chatSortFilter: sortKey }) => {
    return allConversations.sort((a, b) => sortComparator(a, b, sortKey));
  },
  getFilteredConversations: (
    { allConversations, chatSortFilter, appliedFilters },
    _,
    __,
    rootGetters
  ) => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return allConversations
      .filter(conversation => {
        const matchesFilterResult = matchesFilters(
          conversation,
          appliedFilters
        );
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return matchesFilterResult && allowedForRole;
      })
      .sort((a, b) => sortComparator(a, b, chatSortFilter));
  },
  getSelectedChat: ({ selectedChatId, allConversations }) => {
    const selectedChat = allConversations.find(
      conversation => conversation.id === selectedChatId
    );
    return selectedChat || {};
  },
  getSelectedChatAttachments: ({ selectedChatId, attachments }) => {
    return attachments[selectedChatId] || [];
  },
  getChatListFilters: ({ conversationFilters }) => conversationFilters,
  getLastEmailInSelectedChat: (stage, _getters) => {
    const selectedChat = _getters.getSelectedChat;
    const { messages = [] } = selectedChat;
    const lastEmail = [...messages].reverse().find(message => {
      const { message_type: messageType } = message;
      if (message.private) return false;

      return [MESSAGE_TYPE.OUTGOING, MESSAGE_TYPE.INCOMING].includes(
        messageType
      );
    });

    return lastEmail;
  },
  getMineChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUserID = rootGetters.getCurrentUser?.id;

    return _state.allConversations
      .filter(conversation => {
        // «جميع قنوات التعليقات» (conversationType === 'comments') is scoped to
        // comment inboxes — render ONLY those. A real-time websocket update can
        // briefly upsert a non-comment (DM) conversation into the shared store;
        // without this POSITIVE restriction the DM would show here until the
        // next backend refetch (the reported leak). Label views span both, so
        // they keep no exclusion. The bare aggregate tabs (All / Mine /
        // Unassigned) drop comment-inbox convs — those live in their own section.
        if (activeFilters?.conversationType === 'comments') {
          if (
            !isCommentInboxConversation(
              rootGetters,
              conversation,
              activeFilters?.inboxId
            )
          )
            return false;
        } else if (
          !activeFilters?.labels?.length &&
          isCommentInboxConversation(
            rootGetters,
            conversation,
            activeFilters?.inboxId
          )
        )
          return false;
        const { assignee } = conversation.meta;
        const isAssignedToMe = assignee && assignee.id === currentUserID;
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        const isChatMine = isAssignedToMe && shouldFilter;

        return isChatMine;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getAppliedConversationFiltersV2: _state => {
    // TODO: Replace existing one with V2 after migrating the filters to use camelcase
    return _state.appliedFilters.map(camelcaseKeys);
  },
  getAppliedConversationFilters: _state => {
    return _state.appliedFilters;
  },
  getAppliedConversationFiltersQuery: _state => {
    const hasAppliedFilters = _state.appliedFilters.length !== 0;
    return hasAppliedFilters ? filterQueryGenerator(_state.appliedFilters) : [];
  },
  getUnAssignedChats: (_state, _, __, rootGetters) => activeFilters => {
    return _state.allConversations
      .filter(conversation => {
        // «جميع قنوات التعليقات» (conversationType === 'comments') is scoped to
        // comment inboxes — render ONLY those. A real-time websocket update can
        // briefly upsert a non-comment (DM) conversation into the shared store;
        // without this POSITIVE restriction the DM would show here until the
        // next backend refetch (the reported leak). Label views span both, so
        // they keep no exclusion. The bare aggregate tabs (All / Mine /
        // Unassigned) drop comment-inbox convs — those live in their own section.
        if (activeFilters?.conversationType === 'comments') {
          if (
            !isCommentInboxConversation(
              rootGetters,
              conversation,
              activeFilters?.inboxId
            )
          )
            return false;
        } else if (
          !activeFilters?.labels?.length &&
          isCommentInboxConversation(
            rootGetters,
            conversation,
            activeFilters?.inboxId
          )
        )
          return false;
        const isUnAssigned = !conversation.meta.assignee;
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        return isUnAssigned && shouldFilter;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getAllStatusChats: (_state, _, __, rootGetters) => activeFilters => {
    const currentUser = rootGetters.getCurrentUser;
    const currentUserId = rootGetters.getCurrentUser.id;
    const currentAccountId = rootGetters.getCurrentAccountId;

    const permissions = getUserPermissions(currentUser, currentAccountId);
    const userRole = getUserRole(currentUser, currentAccountId);

    return _state.allConversations
      .filter(conversation => {
        // «جميع قنوات التعليقات» (conversationType === 'comments') is scoped to
        // comment inboxes — render ONLY those. A real-time websocket update can
        // briefly upsert a non-comment (DM) conversation into the shared store;
        // without this POSITIVE restriction the DM would show here until the
        // next backend refetch (the reported leak). Label views span both, so
        // they keep no exclusion. The bare aggregate tabs (All / Mine /
        // Unassigned) drop comment-inbox convs — those live in their own section.
        if (activeFilters?.conversationType === 'comments') {
          if (
            !isCommentInboxConversation(
              rootGetters,
              conversation,
              activeFilters?.inboxId
            )
          )
            return false;
        } else if (
          !activeFilters?.labels?.length &&
          isCommentInboxConversation(
            rootGetters,
            conversation,
            activeFilters?.inboxId
          )
        )
          return false;
        const shouldFilter = applyPageFilters(conversation, activeFilters);
        const allowedForRole = applyRoleFilter(
          conversation,
          userRole,
          permissions,
          currentUserId
        );

        return shouldFilter && allowedForRole;
      })
      .sort((a, b) => sortComparator(a, b, _state.chatSortFilter));
  },
  getChatListLoadingStatus: ({ listLoadingStatus }) => listLoadingStatus,
  getAllMessagesLoaded(_state) {
    const [chat] = getSelectedChatConversation(_state);
    return !chat || chat.allMessagesLoaded === undefined
      ? false
      : chat.allMessagesLoaded;
  },
  getUnreadCount(_state) {
    const [chat] = getSelectedChatConversation(_state);
    if (!chat) return [];
    return chat.messages.filter(
      chatMessage =>
        chatMessage.created_at * 1000 > chat.agent_last_seen_at * 1000 &&
        chatMessage.message_type === 0 &&
        chatMessage.private !== true
    ).length;
  },
  getChatStatusFilter: ({ chatStatusFilter }) => chatStatusFilter,
  getChatSortFilter: ({ chatSortFilter }) => chatSortFilter,
  getSelectedInbox: ({ currentInbox }) => currentInbox,
  getConversationById: _state => conversationId => {
    return _state.allConversations.find(
      value => value.id === Number(conversationId)
    );
  },
  getConversationParticipants: _state => {
    return _state.conversationParticipants;
  },
  getConversationLastSeen: _state => {
    return _state.conversationLastSeen;
  },

  getContextMenuChatId: _state => {
    return _state.contextMenuChatId;
  },

  getCopilotAssistant: _state => {
    return _state.copilotAssistant;
  },
};

export default getters;
